import React, { useState } from 'react'
import { Upload, X, Check, Loader } from 'lucide-react'
import { Button } from '../../ui/Button'
import type { StepData } from '../../../services/onboardingService'
import { documentService, DocumentType, type DocumentUploadItem } from '../../../services/documentService'
import { authService } from '../../../services/authService'
import toast from 'react-hot-toast'

interface PendingFile {
  file: File
  type: DocumentType
  description: string
  metadata: string
  validated: boolean
  error?: string
}

interface DocumentUpload {
  file: File | null
  pending: boolean // Archivo seleccionado y validado, listo para subir
  uploaded: boolean
  error?: string
  documentId?: string
}

interface DocumentUploadStepProps {
  onComplete?: (stepData: StepData) => Promise<void>
  stepData?: any
  stepKey?: string
}

const DocumentUploadStep: React.FC<DocumentUploadStepProps> = ({
  onComplete,
}) => {
  const [bankStatements, setBankStatements] = useState<DocumentUpload[]>([
    { file: null, pending: false, uploaded: false },
    { file: null, pending: false, uploaded: false },
    { file: null, pending: false, uploaded: false },
    { file: null, pending: false, uploaded: false },
    { file: null, pending: false, uploaded: false },
    { file: null, pending: false, uploaded: false },
  ])
  const [isUploading, setIsUploading] = useState(false)
  const [isNavigating, setIsNavigating] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)

  const handleFileUpload = (
    index: number,
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0]
    if (!file) return

    // Validar archivo localmente (sin subir)
    const validation = documentService.validateFile(file, DocumentType.BANK_STATEMENT)
    if (!validation.valid) {
      setBankStatements(prev =>
        prev.map((item, i) =>
          i === index ? { 
            file: null, 
            pending: false, 
            uploaded: false, 
            error: validation.error 
          } : item
        )
      )
      return
    }

    // Marcar como pendiente (listo para subir)
    setBankStatements(prev =>
      prev.map((item, i) =>
        i === index ? { 
          file, 
          pending: true, 
          uploaded: false, 
          error: undefined 
        } : item
      )
    )
  }

  const removeFile = async (index: number) => {
    const statement = bankStatements[index]
    
    // Si el documento ya fue subido, eliminarlo del servidor
    if (statement.uploaded && statement.documentId) {
      try {
        await documentService.deleteDocument(statement.documentId)
        toast.success('Documento eliminado')
      } catch (error) {
        console.error('Error deleting document:', error)
        toast.error('Error al eliminar el documento')
        return
      }
    }

    // Limpiar estado local
    setBankStatements(prev =>
      prev.map((item, i) =>
        i === index ? { 
          file: null, 
          pending: false, 
          uploaded: false, 
          error: undefined,
          documentId: undefined 
        } : item
      )
    )
  }

  const uploadedCount = bankStatements.filter(item => item.uploaded).length
  const pendingCount = bankStatements.filter(item => item.pending).length
  const totalReadyFiles = uploadedCount + pendingCount
  const canContinue = totalReadyFiles >= 6 // Require all 6 files

  const handleNext = async () => {
    if (!canContinue) return

    try {
      // Si hay archivos pendientes, subirlos primero
      if (pendingCount > 0) {
        await uploadPendingFiles()
      }

      // Mostrar spinner de navegación
      setIsNavigating(true)

      // Luego completar el paso
      if (onComplete) {
        await onComplete({
          completedAt: new Date().toISOString(),
          data: { bankStatements },
        })
      }
    } catch (error) {
      console.error('Error in step completion:', error)
      toast.error('Error al completar el paso. Intenta nuevamente.')
    } finally {
      setIsNavigating(false)
    }
  }

  const uploadPendingFiles = async () => {
    const pendingFiles = bankStatements
      .map((statement, index) => ({ statement, index }))
      .filter(({ statement }) => statement.pending && statement.file)

    if (pendingFiles.length === 0) return

    setIsUploading(true)
    setUploadProgress(0)

    const months = [
      'enero2025', 'febrero2025', 'marzo2025',
      'abril2025', 'mayo2025', 'junio2025'
    ]

    try {
      // Preparar archivos y metadata para carga por lotes
      const files: File[] = []
      const documents: DocumentUploadItem[] = []

      pendingFiles.forEach(({ statement, index }) => {
        if (statement.file) {
          files.push(statement.file)
          documents.push({
            type: DocumentType.BANK_STATEMENT,
            description: `Estado de cuenta ${months[index]}`,
            metadata: months[index]
          })
        }
      })

      // Subir todos los archivos por lotes
      const creditAppId = await authService.getCurrentCreditApplicationId()
      const response = await documentService.uploadDocuments(
        files,
        documents,
        creditAppId
      )

      // Actualizar estados basado en la respuesta
      setBankStatements(prev => {
        const updated = [...prev]
        
        // Marcar exitosos como uploaded
        response.successful.forEach((uploadedDoc, i) => {
          const pendingFileIndex = pendingFiles[i].index
          updated[pendingFileIndex] = {
            ...updated[pendingFileIndex],
            pending: false,
            uploaded: true,
            documentId: uploadedDoc.id,
            error: undefined
          }
        })

        // Marcar fallidos con error
        response.failed.forEach((failedDoc, i) => {
          const pendingFileIndex = pendingFiles[i].index
          updated[pendingFileIndex] = {
            ...updated[pendingFileIndex],
            pending: false,
            uploaded: false,
            error: failedDoc.error
          }
        })

        return updated
      })

      setUploadProgress(100)

      if (response.successful.length > 0) {
        toast.success(`${response.successful.length} estados de cuenta subidos correctamente`)
      }

      if (response.failed.length > 0) {
        toast.error(`${response.failed.length} archivos fallaron. Revisa los errores.`)
      }

    } catch (error) {
      console.error('Error uploading batch:', error)
      toast.error('Error al subir los archivos. Intenta nuevamente.')
      
      // Marcar todos los pendientes como error
      setBankStatements(prev =>
        prev.map((item, i) => 
          item.pending ? {
            ...item,
            pending: false,
            uploaded: false,
            error: 'Error al subir el archivo. Intenta nuevamente.'
          } : item
        )
      )
    } finally {
      setIsUploading(false)
      setUploadProgress(0)
    }
  }

  const handleCancel = () => {
    // Navigate back or close step
  }

  const months = [
    'enero2025.pdf',
    'febrero2025.pdf',
    'marzo2025.pdf',
    'abril2025.pdf',
    'mayo2025.pdf',
    'junio2025.pdf',
  ]

  return (
    <>
      {/* Navigation Loading Overlay */}
      {isNavigating && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 flex items-center">
            <Loader className="w-6 h-6 text-blue-600 mr-3 animate-spin" />
            <span className="text-gray-900 font-medium">Navegando al siguiente paso...</span>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="text-center mb-8">
        <h1 className="text-2xl font-bold text-gray-900 mb-4">Documentación</h1>
        <p className="text-gray-600">
          Para seguir con tu solicitud, necesitamos que cargues algunos
          documentos clave.
          <br />
          Asegúrate de que sean legibles y estén actualizados.
        </p>
      </div>

      {/* Bank Statements Section */}
      <div className="mb-8">
        <h3 className="text-lg font-semibold text-gray-900 mb-3">
          Estados de cuenta bancarios - Últimos 6 meses
        </h3>
        <p className="text-sm text-gray-600 mb-4">
          Carga los estados de cuenta bancarios de tu negocio de los últimos 6
          meses. Asegúrate que los descargues directo de tu banca en línea y que
          no tengan contraseña para que podamos leerlos.
          <br />
          Formato aceptado: pdf.
        </p>

        <div className="space-y-4">
          {months.map((month, index) => (
            <div key={index} className="flex items-center gap-4">
              {!bankStatements[index].file ? (
                <label className="flex items-center justify-center w-full px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-blue-400 hover:bg-blue-50 transition-colors">
                  <Upload className="w-5 h-5 text-gray-400 mr-2" />
                  <span className="text-gray-600">Buscar archivo</span>
                  <input
                    type="file"
                    accept=".pdf"
                    onChange={e => handleFileUpload(index, e)}
                    className="hidden"
                    disabled={isUploading}
                  />
                </label>
              ) : bankStatements[index].pending ? (
                <div className="flex items-center justify-between w-full p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-center">
                    <Upload className="w-5 h-5 text-blue-600 mr-2" />
                    <span className="text-sm text-blue-800">
                      {bankStatements[index].file?.name} - Listo para subir
                    </span>
                  </div>
                  <button
                    onClick={() => removeFile(index)}
                    className="p-1 text-red-500 hover:text-red-700"
                    disabled={isUploading}
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : bankStatements[index].uploaded ? (
                <div className="flex items-center justify-between w-full p-3 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-center">
                    <Check className="w-5 h-5 text-green-600 mr-2" />
                    <span className="text-sm text-green-800">
                      {bankStatements[index].file?.name} - Subido
                    </span>
                  </div>
                  <button
                    onClick={() => removeFile(index)}
                    className="p-1 text-red-500 hover:text-red-700"
                    disabled={isUploading}
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <div className="flex items-center justify-between w-full p-3 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex items-center">
                    <X className="w-5 h-5 text-red-600 mr-2" />
                    <span className="text-sm text-red-800">Error en archivo</span>
                  </div>
                  <button
                    onClick={() => removeFile(index)}
                    className="p-1 text-red-500 hover:text-red-700"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              )}

              <div className="flex-shrink-0 w-32 text-right">
                <span className="text-sm text-gray-500">{month}</span>
              </div>
            </div>
          ))}
        </div>

        {/* Indicador de progreso durante la carga */}
        {isUploading && (
          <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-center mb-2">
              <Loader className="w-5 h-5 text-blue-600 mr-2 animate-spin" />
              <span className="text-sm text-blue-800">Subiendo archivos...</span>
            </div>
            <div className="w-full bg-blue-200 rounded-full h-2">
              <div 
                className="bg-blue-600 h-2 rounded-full transition-all duration-300" 
                style={{ width: `${uploadProgress}%` }}
              ></div>
            </div>
          </div>
        )}

        {/* Estados de los archivos */}
        {totalReadyFiles > 0 && !isUploading && (
          <div className="mt-4 space-y-2">
            {uploadedCount > 0 && (
              <p className="text-xs text-green-600">
                ✅ {uploadedCount} archivo{uploadedCount !== 1 ? 's' : ''} subido{uploadedCount !== 1 ? 's' : ''} correctamente
              </p>
            )}
            {pendingCount > 0 && (
              <p className="text-xs text-blue-600">
                📋 {pendingCount} archivo{pendingCount !== 1 ? 's' : ''} listo{pendingCount !== 1 ? 's' : ''} para subir
              </p>
            )}
            {totalReadyFiles === 6 && (
              <p className="text-xs text-green-600 font-medium">
                🎉 Todos los archivos están listos. ¡Haz clic en Continuar para subirlos!
              </p>
            )}
          </div>
        )}

        {bankStatements.some(item => item.error) && (
          <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-xs text-red-600 font-medium mb-1">Errores encontrados:</p>
            {bankStatements.map((item, index) => 
              item.error && (
                <p key={index} className="text-xs text-red-600">
                  • {months[index]}: {item.error}
                </p>
              )
            )}
          </div>
        )}
      </div>

      {/* Action Buttons */}
      <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 pt-6">
        <Button
          type="button"
          variant="outline"
          onClick={handleCancel}
          className="flex-1"
          disabled={isUploading}
        >
          Cancelar
        </Button>
        <Button 
          onClick={handleNext} 
          disabled={!canContinue || isUploading || isNavigating} 
          className="flex-1"
        >
          {isUploading ? (
            <>
              <Loader className="w-4 h-4 mr-2 animate-spin" />
              Subiendo...
            </>
          ) : isNavigating ? (
            <>
              <Loader className="w-4 h-4 mr-2 animate-spin" />
              Navegando...
            </>
          ) : pendingCount > 0 ? (
            `Subir y Continuar (${pendingCount} archivos)`
          ) : (
            'Continuar'
          )}
        </Button>
      </div>
    </>
  )
}

export default DocumentUploadStep
